import React, { useEffect, useMemo, useRef, useState } from 'react';
import { approverPhrase } from './approver';
import { useMutation, useQuery } from '@apollo/client';
import { Link, useNavigate } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { CAMPAIGN_DUE_WORK, daysFromToday, dueDay, monthDay } from '../reach/campaignCards';
import { UNPUBLISH_TINYCMS_CONTENT } from '../../graphql';
import { showInkToast } from './InkToast';
import { homeNotice } from './homeGap';
import { useAgentWork } from './AgentStatus';
import { agentRowWords, agentWorkLink } from './agentWork';
import {
  groupApprovals,
  groupSummary,
  postApprovedBy,
  postRuleLabel,
  rowPlace,
  rowText,
  wentOutByRule,
} from './wentOut';
import { InkSkeleton } from './InkSkeleton';
import { RollNumber } from '../common/RollNumber';
import { SiteHomeNotice, useSiteHomeGap } from './SiteHomeNotice';
import { useAddons } from './addons';
import { TodaySiteHealth, TodaySiteHealthCard } from './SiteHealth';
import { TodayAgentControls } from './TodayAgentControls';
import { useTinycmsSites } from './sites';
import {
  AnalyticsTopPage,
  TodayApproval,
  TodayCampaign,
  TodayKind,
  TodayPerformance,
  TodayScheduled,
  useToday,
  WebsiteAnalytics,
} from './todayData';
import { pageName } from './analyticsMath';
import { timelineRows, timelineWindow, TIMELINE_WEEKS } from './todayTimeline';
import { todayMenuNotice } from './navChecks';
import { channelMark, channelName, postText, REACH_CHANNELS, toDate } from '../reach/reach';
import { compact, REACH_SITE_VISITS, ReachVisits, viewsShown } from '../reach/reachVisits';
import { askInk } from './InkConfirm';
import { HomeTag, isHomePath, titledHome } from './HomeTag';
import { ApprovalsReviewButton, useApprovals } from '../approvals/ApprovalsReview';

/**
 * tinycms → Today, the app's home ("TinyCMS UX optimization review", App
 * Prototype, Monte Carlo demo): the date and how many items await approval,
 * then Performance (the last 7 days against the 7 before), the next eight
 * weeks of campaigns, and Recent changes beside Scheduled. Approvals moved
 * to the Approvals review (design 2026-10-27): the header's "Review N
 * approvals →" opens it, as the top bar and the Today tab do. Built from
 * tinycmsToday (approvals, checks, the week's form responses and reader
 * questions) and Reach's calendar and campaigns. Pageviews and top pages
 * come from Google Analytics once it's connected; social numbers aren't
 * recorded yet, so those parts are grey shapes (never a "Coming soon" note).
 * Below 900px the sections are one row of cards you swipe through (TodaySwipe,
 * App Prototype's phone Today).
 */

const DAY = 86400000;
/** Rows shown in Recent changes and Scheduled at desktop widths; the section links open the rest. */
const CHANGES_SHOWN = 4;
const SCHEDULED_SHOWN = 3;

const time = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const weekday = (d: Date) => d.toLocaleDateString([], { weekday: 'short' });

function dayLabel(date = new Date()) {
  return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
}

/** "Wed, Nov 4": what fits beside the review button on a narrow phone. */
function shortDayLabel(date = new Date()) {
  return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

/** "Today 6:00 PM", "Tomorrow 9:00 AM", "Sun 9:00 AM", "Oct 30, 9:00 AM". */
function when(d: Date, now = new Date()) {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const days = Math.floor((d.getTime() - start.getTime()) / DAY);
  if (days === 0) return `Today ${time(d)}`;
  if (days === 1) return `Tomorrow ${time(d)}`;
  if (days > 1 && days < 7) return `${weekday(d)} ${time(d)}`;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time(d)}`;
}

const reviewHref = (kind: TodayKind | 'dataset', id: string) =>
  kind === 'article'
    ? `/content/${id}`
    : kind === 'page'
      ? `/content?view=pages&page=${id}`
      : kind === 'form'
        ? `/content?view=forms&form=${id}`
        : '/content?view=manage&area=design';

/** One row of Recent changes: what went out today, and Undo while it's live. */
interface ChangeRow {
  key: string;
  at: Date;
  text: string;
  obj: string;
  /** The site's home page: its title alone rarely says so. */
  home?: boolean;
  /** The owner's rule that let it out without an approval ("Your rule: …"). */
  rule?: string;
  undo?: () => Promise<void>;
  undone?: boolean;
}

// ── Sections ────────────────────────────────────────────────────────────────

/** A section's rule and label line: "APPROVALS  3 awaiting  ·····  Approval history →". */
const SectionHead: React.FC<{
  label: string;
  sub?: string;
  /** Its place in Today's entrance (ink.css [data-enter]), when it has its own. */
  enter?: number;
  children?: React.ReactNode;
}> = ({ label, sub, enter, children }) => (
  <div className="today-sec-head" data-enter={enter}>
    <h2 className="today-sec-label">{label}</h2>
    {sub ? <span className="today-sec-sub">{sub}</span> : null}
    {children}
  </div>
);

/** "Sun 4" … "Today": the ends of a 7-day trend line. */
function trendEnds(perf: TodayPerformance | null) {
  const from = perf ? new Date(perf.from) : new Date(Date.now() - 6 * DAY);
  const first = new Date(from.getTime() + DAY);
  return [`${weekday(first)} ${first.getDate()}`, 'Today'];
}

/**
 * Where a number will be once there is one (pageviews, impressions): grey
 * shapes in its place. Nothing moves, so it doesn't read as loading. A tag
 * over them only when something is happening ("Connected · Bringing in your
 * numbers"); `what` null shows the shapes alone. Not connected at all says so
 * in words instead (NoAnalytics).
 */
const Placeholder: React.FC<{
  what: React.ReactNode | null;
  label: string;
  ends: string[];
  tag?: string;
}> = ({ what, label, ends, tag = '' }) => (
  <div className="today-soon">
    <div aria-hidden="true">
      <div className="today-perf-big">
        <span className="ink-ghost" style={{ width: 150, height: 40 }} />
        <span className="ink-ghost" style={{ width: 44, height: 14 }} />
      </div>
      <div className="today-perf-unit">{label}</div>
      <svg className="today-spark" viewBox="0 0 300 60" preserveAspectRatio="none">
        <polyline
          points="0,40 50,38 100,42 150,34 200,30 250,36 300,32"
          fill="none"
          strokeWidth="1.5"
          strokeDasharray="3 3"
          vectorEffect="non-scaling-stroke"
        />
        <polyline
          points="0,34 50,28 100,36 150,20 200,14 250,18 300,10"
          fill="none"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <div className="today-spark-ends">
        <span>{ends[0]}</span>
        <span>{ends[1]}</span>
      </div>
    </div>
    {what === null ? null : (
      <div className="ink-soon-tag">
        <span>{tag}</span>
        {what}
      </div>
    )}
  </div>
);

/**
 * A trend line's points in the 300×60 box, between the lowest and highest
 * value of both weeks, so a change shows as a slope rather than a flat line.
 */
function sparkPoints(values: number[], min: number, max: number) {
  const n = values.length;
  const span = Math.max(1, max - min);
  return values
    .map(
      (v, i) =>
        `${n > 1 ? Math.round((i * 300) / (n - 1)) : 0},${Math.round(54 - ((v - min) / span) * 46)}`
    )
    .join(' ');
}

const pageLink = (p: AnalyticsTopPage) =>
  p.kind === 'article' && p.id
    ? `/content/${p.id}`
    : p.kind === 'page' && p.id
      ? `/content?view=pages&page=${p.id}`
      : null;

/**
 * A week's number against the week before: the count, its change, and a
 * trend line per day (dashed: the week before, when there is one).
 */
const TrendNumber: React.FC<{
  value: { count: number; previous: number };
  unit: string;
  now: number[];
  before: number[];
  ends: string[];
}> = ({ value, unit, now, before, ends }) => {
  const pct = value.previous
    ? Math.round(((value.count - value.previous) / value.previous) * 100)
    : null;
  const max = Math.max(1, ...now, ...before);
  const min = Math.min(...now, ...before, max);
  return (
    <>
      <div className="today-perf-big">
        <span className="today-perf-n">
          <RollNumber value={value.count} />
        </span>
        {pct === null ? null : (
          <span className="today-perf-chg" data-tone={pct < 0 ? 'down' : 'up'}>
            {pct < 0 ? '▼' : '▲'} {Math.abs(pct)}%
          </span>
        )}
        {pct === null ? null : <span className="today-perf-vs today-sw-only">vs last week</span>}
      </div>
      <div className="today-perf-unit">{unit}</div>
      <svg
        className="today-spark today-spark-live"
        viewBox="0 0 300 60"
        preserveAspectRatio="none"
        role="img"
        aria-label={`${unit} per day, ${now.join(', ')}`}
      >
        {before.length ? (
          <polyline
            points={sparkPoints(before, min, max)}
            fill="none"
            strokeWidth="1.5"
            strokeDasharray="3 3"
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
        <polyline
          points={sparkPoints(now, min, max)}
          fill="none"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <div className="today-spark-ends">
        <span>{ends[0]}</span>
        <span>{ends[1]}</span>
      </div>
    </>
  );
};

/**
 * The website's last 7 days from Google Analytics (as last synced): pageviews
 * against the 7 before, a trend line (dashed: the week before), and the top pages.
 */
const WebsiteNumbers: React.FC<{ website: WebsiteAnalytics; ends: string[] }> = ({
  website,
  ends,
}) => {
  const pv = website.ranges!.pageviews;
  return (
    <TrendNumber
      value={pv}
      unit={pv.count === 1 ? 'pageview' : 'pageviews'}
      now={website.daily.map((d) => d.pageviews)}
      before={website.previousDaily.map((d) => d.pageviews)}
      ends={ends}
    />
  );
};

/** "impression" or "impressions" (no count; word() above includes it). */
const word = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);

/**
 * Social, last 7 days (Reach → Analytics' numbers for the week): impressions
 * (the views the networks report) against the week before, or visits to the
 * site from posts when no network reports views yet; then clicks, engagements
 * and sign-ups where they exist; then the posts seen most. Nothing to show yet:
 * the grey shapes alone, never made-up numbers or a "Coming soon" note.
 */
const SocialNumbers: React.FC<{ ends: string[]; hasChannels: boolean }> = ({
  ends,
  hasChannels,
}) => {
  const { accountId } = useAccount();
  const { data } = useQuery<{ reachSiteVisits: ReachVisits }>(REACH_SITE_VISITS, {
    variables: { accountId, days: 7 },
    skip: !accountId || !hasChannels,
    fetchPolicy: 'cache-and-network',
  });
  const v = data?.reachSiteVisits?.days === 7 ? data.reachSiteVisits : null;
  const clicks = Boolean(v?.connected && v.syncedAt);
  // Views can be reported here, even with none this week: real zeros, never the
  // grey shapes, which read as stuck loading (backlog f89f0ce4). The shapes stay
  // only where nothing can be reported yet.
  const tracked = viewsShown(v);
  if (!v || (!tracked && !clicks && !v.signups.count)) {
    return (
      <>
        <Placeholder what={null} label="impressions" ends={ends} />
        <div className="today-stats" aria-hidden="true">
          <Stat n={null} label="clicks to your site" />
          <Stat n={null} label="engagements" />
        </div>
        <GhostTop label="Top posts" />
      </>
    );
  }
  const visitsPerDay = v.daily.map((d) => Object.values(d.bySource).reduce((a, b) => a + b, 0));
  const posts = [...v.posts]
    .sort((a, b) => (b.views ?? -1) - (a.views ?? -1) || b.visits - a.visits)
    .slice(0, 3);
  // Impressions lead when there are some, or when there are no clicks to show instead.
  const leadViews = v.viewsReported || (tracked && !clicks);
  return (
    <>
      {leadViews ? (
        <TrendNumber
          value={v.impressions}
          unit={word(v.impressions.count, 'impression')}
          now={v.dailyViews.map((d) => d.views)}
          before={v.previousDailyViews.map((d) => d.views)}
          ends={ends}
        />
      ) : clicks ? (
        <TrendNumber
          value={v.visits}
          unit={`${word(v.visits.count, 'visit')} to your site from posts`}
          now={visitsPerDay}
          before={[]}
          ends={ends}
        />
      ) : (
        <Placeholder what={null} label="impressions" ends={ends} />
      )}
      <div className="today-stats">
        {leadViews && clicks ? (
          <Stat
            n={v.visits.count}
            label={word(v.visits.count, 'click to your site', 'clicks to your site')}
            to="/reach/analytics"
          />
        ) : null}
        {tracked ? (
          <Stat n={v.engagements.count} label={word(v.engagements.count, 'engagement')} />
        ) : null}
        {v.signups.count ? (
          <Stat n={v.signups.count} label={word(v.signups.count, 'sign-up')} />
        ) : null}
      </div>
      <div className="today-perf-top">Top posts</div>
      {posts.length ? (
        <div className="today-perf-list">
          {posts.map((p) => (
            <React.Fragment key={p.postId}>
              <span className="today-perf-rank" title={channelName(p.platform)}>
                {channelMark(p.platform)}
              </span>
              <Link to="/reach/analytics" className="today-perf-page">
                {p.text || 'A post with no text'}
              </Link>
              <span className="today-perf-views">
                {p.views != null
                  ? compact(p.views)
                  : `${p.visits.toLocaleString()} ${word(p.visits, 'click')}`}
              </span>
            </React.Fragment>
          ))}
        </div>
      ) : (
        <div className="today-perf-none">None of your posts were seen this week.</div>
      )}
    </>
  );
};

/** The three most-viewed pages and articles this week. */
const TopPages: React.FC<{ pages: AnalyticsTopPage[] }> = ({ pages }) => (
  <>
    <div className="today-perf-top">Top pages and articles</div>
    {pages.length ? (
      <div className="today-perf-list">
        {pages.slice(0, 3).map((p, i) => {
          const href = pageLink(p);
          return (
            <React.Fragment key={p.path}>
              <span className="today-perf-rank">{i + 1}</span>
              {href ? (
                <Link to={href} className="today-perf-page">
                  {pageName(p)}
                  {p.title && isHomePath(p.path) ? <HomeTag title={p.title} /> : null}
                </Link>
              ) : (
                <span className="today-perf-page">
                  {pageName(p)}
                  {p.title && isHomePath(p.path) ? <HomeTag title={p.title} /> : null}
                </span>
              )}
              <span className="today-perf-views">{p.views.toLocaleString()}</span>
            </React.Fragment>
          );
        })}
      </div>
    ) : (
      <div className="today-perf-none">No pages were visited this week.</div>
    )}
  </>
);

/** Three grey rows where the top pages or top posts will be. */
const GhostTop: React.FC<{ label: string }> = ({ label }) => (
  <>
    <div className="today-perf-top">{label}</div>
    <div className="today-perf-list" aria-hidden="true">
      {[78, 64, 70].map((w, i) => (
        <React.Fragment key={i}>
          <span className="today-perf-rank">{i + 1}</span>
          <span className="ink-ghost" style={{ width: `${w}%`, height: 10 }} />
          <span className="ink-ghost" style={{ width: 36, height: 10 }} />
        </React.Fragment>
      ))}
    </div>
  </>
);

/** A small number and what it counts ("14 form responses"). */
const Stat: React.FC<{ n: number | null; label: string; to?: string }> = ({ n, label, to }) => {
  const body = (
    <>
      <div className="today-stat-n">
        {n == null ? (
          // Same line box as a number, so a grey one lines up with a real one.
          <span className="ink-ghost" style={{ width: 40, height: 20, verticalAlign: -3 }} />
        ) : (
          <RollNumber value={n} />
        )}
      </div>
      <div className="today-stat-label">{label}</div>
    </>
  );
  return to ? (
    <Link to={to} className="today-stat">
      {body}
    </Link>
  ) : (
    <div className="today-stat">{body}</div>
  );
};

/**
 * Website, last 7 days against the 7 before: pageviews and top pages from Google
 * Analytics once it's connected (a way to connect it until then), and the form
 * responses and reader questions TinyCMS records. In Performance's grid at
 * desktop widths, its own card on a phone (with its own analytics link).
 */
const WebsiteCell: React.FC<{
  host: string | null;
  perf: TodayPerformance | null;
  loading: boolean;
  readerQuestions: boolean;
}> = ({ host, perf, loading, readerQuestions }) => {
  const ends = trendEnds(perf);
  // Google Analytics is set up and fixed in one place, on every plan.
  const connectTo = '/content?view=manage&area=code#analytics';
  const website = perf?.website;
  const n = (v?: { count: number }) => (loading || !v ? null : v.count);
  return (
    <div className="today-perf-cell">
      <div className="today-perf-head">
        <span className="today-perf-name">Website</span>
        {host ? <span className="today-perf-where">{host}</span> : null}
        <Link to="/content?view=analytics" className="today-sec-link today-sw-only">
          Website Analytics <span aria-hidden>→</span>
        </Link>
      </div>
      {website?.ranges ? (
        <WebsiteNumbers website={website} ends={ends} />
      ) : website?.connection ? (
        <Placeholder
          tag="Connected"
          what="Bringing in your numbers"
          label="pageviews"
          ends={ends}
        />
      ) : website ? (
        // No Google Analytics: say so in words, not shapes that read as
        // loading (Prototype, backlog e0d8023a).
        <>
          <div className="today-note-title">Connect Google Analytics to see pageviews here.</div>
          <div className="today-note-body">
            Pageviews, the weekly trend and your top pages come from Google Analytics. Form
            responses and reader questions don’t need it.
          </div>
          <div style={{ display: 'flex', marginTop: 20 }}>
            <Link to={connectTo} className="ink-btn ink-btn-primary">
              Connect Google Analytics
            </Link>
          </div>
        </>
      ) : (
        <Placeholder what={null} label="pageviews" ends={ends} />
      )}
      {website?.connection?.status === 'reconnect' ? (
        <Link to={connectTo} className="today-perf-warn">
          Google Analytics needs connecting again →
        </Link>
      ) : null}
      {(perf?.search?.issues || []).map((i) => (
        // What Google itself reports (the daily site check's google_* findings).
        <Link
          key={i.code}
          to="/content?view=manage&area=site#search"
          className="today-perf-warn"
          title={i.message}
        >
          {i.code === 'google_home_not_indexed'
            ? 'Google hasn’t indexed your home page →'
            : i.code === 'google_sitemap_error'
              ? 'Google can’t read your sitemap →'
              : i.code === 'google_clicks_drop'
                ? 'Clicks from Google fell sharply →'
                : 'We couldn’t prove your site to Google yet →'}
        </Link>
      ))}
      <div className="today-stats">
        <Stat
          n={n(perf?.formResponses)}
          label={perf?.formResponses.count === 1 ? 'form response' : 'form responses'}
          to="/content?view=forms"
        />
        {readerQuestions ? (
          <Stat
            n={n(perf?.readerQuestions)}
            label={perf?.readerQuestions.count === 1 ? 'reader question' : 'reader questions'}
          />
        ) : null}
        {perf?.search?.live && perf.search.ranges ? (
          // From Google Search Console, as last synced (Manage › Site › Search).
          <Stat
            n={loading ? null : perf.search.ranges.clicks.count}
            label={
              perf.search.ranges.clicks.count === 1 ? 'click from Google' : 'clicks from Google'
            }
            to="/content?view=analytics#search"
          />
        ) : null}
      </div>
      {website?.ranges ? (
        <TopPages pages={website.topPages} />
      ) : !website || website.connection ? (
        <GhostTop label="Top pages and articles" />
      ) : null}
    </div>
  );
};

/**
 * Social, with Reach: impressions, clicks, engagements and top posts for the
 * connected networks (grey shapes until there are some), or a way to connect a
 * channel when there's none. Without Reach, no Social at all (owner 2026-10-23).
 */
const SocialCell: React.FC<{
  networks: string[];
  channelsLoading: boolean;
  ends: string[];
}> = ({ networks, channelsLoading, ends }) => (
  <div className="today-perf-cell">
    <div className="today-perf-head">
      <span className="today-perf-name">Social</span>
      {channelsLoading ? null : (
        <span
          className="today-perf-where"
          title={networks.length > NETWORKS_SHOWN ? networks.join(' · ') : undefined}
        >
          {networks.length ? networksLine(networks) : 'No channels connected'}
        </span>
      )}
      {networks.length ? (
        <Link to="/reach/analytics" className="today-sec-link today-sw-only">
          Social Analytics <span aria-hidden>→</span>
        </Link>
      ) : null}
    </div>
    {networks.length || channelsLoading ? (
      <SocialNumbers ends={ends} hasChannels={networks.length > 0} />
    ) : (
      // No channel: say so in words, like Website without Google
      // Analytics, not shapes that read as loading (Prototype).
      <>
        <div className="today-note-title">Connect a channel to see how your posts do.</div>
        <div className="today-note-body">
          Impressions, clicks back to your site and your top posts come from the channels you
          connect, like X, Threads or LinkedIn.
        </div>
        <div style={{ display: 'flex', marginTop: 20 }}>
          <Link to="/reach/channels" className="ink-btn ink-btn-primary">
            Connect a channel
          </Link>
        </div>
      </>
    )}
  </div>
);

/**
 * Performance at desktop widths, last 7 days against the 7 before: Website,
 * Site health (on every plan), then Social. On a phone each is its own card.
 */
const Performance: React.FC<{
  /** Social numbers to show (Reach and a connected channel). */
  socialOn: boolean;
  website: React.ReactNode;
  social: React.ReactNode;
}> = ({ socialOn, website, social }) => (
  // Analytics links live only in the section header (App Prototype, "Today:
  // links in section headers"): one "Analytics →", or with social numbers to
  // show Website and Social stacked. Never both.
  <section className="today-sec today-perf-sec" aria-label="Performance">
    <SectionHead label="Performance" sub="Last 7 days, compared with the 7 before" enter={1}>
      {socialOn ? (
        <span className="today-sec-links">
          <Link to="/content?view=analytics" className="today-sec-link">
            Website Analytics <span aria-hidden>→</span>
          </Link>
          <Link to="/reach/analytics" className="today-sec-link">
            Social Analytics <span aria-hidden>→</span>
          </Link>
        </span>
      ) : (
        <Link to="/content?view=analytics" className="today-sec-link">
          Analytics <span aria-hidden>→</span>
        </Link>
      )}
    </SectionHead>
    <div className="today-perf-wrap" data-enter={2}>
      <div className="today-perf">
        {website}
        <TodaySiteHealth />
        {social}
      </div>
    </div>
  </section>
);

/** Where on the timeline (0–1) a moment falls, kept inside it. */
const at = (t: number, from: number, to: number) =>
  Math.min(1, Math.max(0, (t - from) / (to - from)));

/** A campaign's open work from its board (campaignDueWork), for the due marks. */
export interface DueWork {
  campaignId: string;
  name: string;
  cards: Array<{
    key: string;
    kind: string;
    text: string;
    dueDate: string | null;
    dueBase: string | null;
    dueOffset: number;
    missing: string[];
    /** Said instead of "Needs …" (a task: "Not done yet"). */
    need?: string | null;
    note: string;
  }>;
}

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface DueMark {
  key: string;
  at: number;
  tone: 'late' | 'soon' | 'later' | 'ready';
  head: string;
  count: string;
  items: Array<{ kind: string; text: string; need: string; ready: boolean }>;
}

/**
 * A campaign's due marks (campaign board, 2026-10-23): one per day something
 * of it is due, inside the window — red overdue, amber within 2 days, ink
 * later, green when everything due that day is ready.
 */
function dueMarks(work: DueWork | undefined, from: number, to: number, now: Date): DueMark[] {
  if (!work) return [];
  const byDay = new Map<number, DueWork['cards']>();
  for (const c of work.cards) {
    const d = dueDay(c);
    if (!d) continue;
    const n = daysFromToday(d, now);
    const list = byDay.get(n) || [];
    list.push(c);
    byDay.set(n, list);
  }
  return [...byDay.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([n, list]) => {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + n);
      const allReady = list.every((c) => !c.missing.length);
      const tone: DueMark['tone'] = allReady ? 'ready' : n < 0 ? 'late' : n <= 2 ? 'soon' : 'later';
      const label = `${DOW[d.getDay()]} ${monthDay(d)}`;
      return {
        key: `${work.campaignId}:${n}`,
        at: (d.getTime() + 12 * 3600000 - from) / (to - from),
        tone,
        head: n < 0 ? `Overdue · ${label}` : n === 0 ? `Due today · ${label}` : `Due ${label}`,
        count: `${list.length} ${list.length === 1 ? 'item' : 'items'}`,
        items: list.slice(0, 5).map((c) => ({
          kind: c.kind,
          text: c.text,
          ready: !c.missing.length,
          need:
            c.need ||
            (c.missing.length
              ? `Needs ${c.missing.map((m) => m.toLowerCase()).join(', ')}`
              : '✓ Ready'),
        })),
      };
    })
    .filter((m) => m.at >= 0 && m.at <= 1);
}

/**
 * Campaigns over the next eight weeks, from this week's Monday: always-on
 * campaigns as one thin bar across, dated ones from start to end, and a
 * campaign waiting on approval hatched, with "Approve by" its start. Marks on
 * a dated campaign's bar show when its work is due; hover one for what.
 */
const CampaignTimeline: React.FC<{
  all: TodayCampaign[];
  waiting: TodayCampaign[];
  loading: boolean;
  due?: DueWork[];
}> = ({ all, waiting, loading, due = [] }) => {
  const navigate = useNavigate();
  const [hoverMark, setHoverMark] = useState<string | null>(null);
  const now = new Date();
  const win = timelineWindow(now);
  // Sharing rules aren't campaigns (no start or end): they live on Reach →
  // Sharing rules only (Motion and Brand spec, 2026-10-27).
  const rows = timelineRows(
    all.filter((c) => c.kind !== 'always_on'),
    new Set(waiting.map((c) => c.id)),
    win,
    now
  );
  const from = win.from.getTime();
  const to = win.to.getTime();
  const todayAt = at(now.getTime(), from, to);
  const marksOf = (id: string) =>
    dueMarks(
      due.find((d) => d.campaignId === id),
      from,
      to,
      now
    );
  const anyMarks = rows.some((r) => !r.always && marksOf(r.id).length > 0);
  const pos = (f: number) =>
    `calc(var(--tl-label) + var(--tl-gap) + (100% - var(--tl-label) - var(--tl-gap)) * ${f.toFixed(4)})`;
  // A date the Today line would cross is hidden (backlog 5582509a: "Oc|t 19"
  // at 375px); the next one shown then carries the month.
  const ticksRef = useRef<HTMLDivElement>(null);
  const [crossed, setCrossed] = useState<{ hide: number; month: number } | null>(null);
  // Steady between renders (todayAt moves by the millisecond), so the effect reruns only when it moves.
  const todayFrac = Math.round(todayAt * 2000) / 2000;
  useEffect(() => {
    const box = ticksRef.current;
    if (!box) return;
    const measure = () => {
      const spans = Array.from(box.children) as HTMLElement[];
      const at = todayFrac * box.clientWidth;
      const hide = spans.findIndex(
        (el) =>
          el.offsetWidth > 0 && at >= el.offsetLeft - 4 && at <= el.offsetLeft + el.offsetWidth + 4
      );
      // offsetWidth 0: hidden by CSS (every other date on a phone) or by us.
      const month =
        hide < 0
          ? -1
          : spans.findIndex((el, i) => i > hide && getComputedStyle(el).display !== 'none');
      const next = hide < 0 ? null : { hide, month };
      setCrossed((was) => (was?.hide === next?.hide && was?.month === next?.month ? was : next));
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(box);
    return () => ro?.disconnect();
  }, [todayFrac, loading]);
  const ticks = Array.from({ length: TIMELINE_WEEKS }, (_, i) => {
    const d = new Date(win.from);
    d.setDate(d.getDate() + i * 7);
    const month = i === 0 || d.getDate() <= 7 || i === crossed?.month;
    return {
      left: (i / TIMELINE_WEEKS) * 100,
      label: month
        ? d.toLocaleDateString([], { month: 'short', day: 'numeric' })
        : String(d.getDate()),
    };
  });
  return (
    <section className="today-sec" aria-label="Campaigns" data-enter={3}>
      <SectionHead label="Campaigns" sub={`Next ${TIMELINE_WEEKS} weeks`}>
        <Link to="/campaigns" className="today-sec-link">
          Campaigns <span aria-hidden>→</span>
        </Link>
      </SectionHead>
      {loading ? (
        <InkSkeleton rows={2} label="Loading campaigns" style={{ marginTop: 20 }} />
      ) : !rows.length ? (
        <p className="today-tl-none">
          No campaigns in the next {TIMELINE_WEEKS} weeks.{' '}
          <Link to="/campaigns?new=dated">Plan one</Link>
        </p>
      ) : (
        <>
          <div className="today-tl">
            <div className="today-tl-now" style={{ left: pos(todayAt) }} />
            <span className="today-tl-pill" style={{ left: pos(todayAt) }}>
              Today
            </span>
            <div className="today-tl-grid">
              <span />
              <div className="today-tl-ticks" ref={ticksRef}>
                {ticks.map((t, i) => (
                  <span
                    key={t.left}
                    data-odd={i % 2 ? true : undefined}
                    style={{
                      left: `${t.left}%`,
                      visibility: i === crossed?.hide ? 'hidden' : undefined,
                    }}
                  >
                    {t.label}
                  </span>
                ))}
              </div>
              {rows.length
                ? rows.map((r) => (
                    <React.Fragment key={r.id}>
                      <Link to={`/campaigns?campaign=${r.id}`} className="today-tl-name">
                        <div className="today-tl-title">
                          {r.paint ? (
                            <i className="ccp-dot" style={{ background: r.paint.chip }} />
                          ) : null}
                          {r.name}
                        </div>
                        <div className="today-tl-sub" style={{ color: r.color }}>
                          {r.sub}
                        </div>
                      </Link>
                      <div
                        className="today-tl-track"
                        data-always={r.always ? 'true' : undefined}
                        data-range={!r.always && r.range ? 'true' : undefined}
                      >
                        {/* On a phone the bar runs full width under the name, with its own today mark (backlog 4dcaef5b). */}
                        <i className="today-tl-mark" style={{ left: `${todayAt * 100}%` }} />
                        {r.always ? (
                          <div className="today-tl-bar" data-always="true" data-tone={r.tone} />
                        ) : (
                          <>
                            <div
                              className="today-tl-bar"
                              data-tone={r.tone}
                              data-painted={r.paint ? 'true' : undefined}
                              data-before={r.before || undefined}
                              data-after={r.after || undefined}
                              title={r.range}
                              style={{
                                left: `${r.start * 100}%`,
                                width: `${Math.max(0.6, (r.end - r.start) * 100)}%`,
                                ...(r.paint
                                  ? { background: r.paint.background, border: r.paint.border }
                                  : {}),
                              }}
                            />
                            {r.range
                              ? (() => {
                                  // Never under a due mark (UX review round 20: "ct 26 –
                                  // Oct 30"): after the later of the bar's end and its
                                  // last mark; with no room there, before the bar if no
                                  // mark sits there, else below the bar.
                                  const ats = marksOf(r.id).map((m) => m.at);
                                  const after = Math.max(r.end, ...ats);
                                  const clearBefore = ats.every((x) => x >= r.start);
                                  // Runs past the window (UX review round 24: "Oct 2|4 –
                                  // Dec 31" under the Today line at the left edge):
                                  // under the bar's right end, with "→".
                                  const style: React.CSSProperties =
                                    after <= 0.72
                                      ? { left: `calc(${after * 100}% + 10px)` }
                                      : r.start >= 0.28 && clearBefore
                                        ? { right: `calc(${(1 - r.start) * 100}% + 10px)` }
                                        : r.after
                                          ? { right: 0 }
                                          : { left: `${Math.min(r.start, 0.55) * 100}%` };
                                  const below = after > 0.72 && !(r.start >= 0.28 && clearBefore);
                                  return (
                                    <span
                                      className="today-tl-range"
                                      data-below={below ? 'true' : undefined}
                                      style={style}
                                    >
                                      {r.before ? '← ' : ''}
                                      {r.range}
                                      {r.after ? ' →' : ''}
                                    </span>
                                  );
                                })()
                              : null}
                            {marksOf(r.id).map((m) => (
                              <span
                                key={m.key}
                                className="today-tl-due"
                                data-tone={m.tone}
                                style={{ left: `${m.at * 100}%` }}
                                role="button"
                                tabIndex={0}
                                aria-label={`${m.head}: ${m.count}`}
                                onMouseEnter={() => setHoverMark(m.key)}
                                onMouseLeave={() => setHoverMark((k) => (k === m.key ? null : k))}
                                onFocus={() => setHoverMark(m.key)}
                                onBlur={() => setHoverMark((k) => (k === m.key ? null : k))}
                                onClick={() => navigate(`/campaigns?campaign=${r.id}`)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') navigate(`/campaigns?campaign=${r.id}`);
                                }}
                              >
                                {hoverMark === m.key ? (
                                  <span className="today-tl-tip" role="tooltip">
                                    <span className="today-tl-tip-head">
                                      <span data-tone={m.tone}>{m.head}</span>
                                      <span>{m.count}</span>
                                    </span>
                                    {m.items.map((it, i) => (
                                      <span className="today-tl-tip-item" key={i}>
                                        <span className="today-tl-tip-line">
                                          <span className="cb-chip">{it.kind}</span>
                                          <span className="today-tl-tip-text">{it.text}</span>
                                        </span>
                                        <span
                                          className="today-tl-tip-need"
                                          data-ok={it.ready ? 'true' : undefined}
                                        >
                                          {it.need}
                                        </span>
                                      </span>
                                    ))}
                                    <span className="today-tl-tip-foot">
                                      Click to open the board
                                    </span>
                                  </span>
                                ) : null}
                              </span>
                            ))}
                            {r.range ? (
                              <span
                                className="today-tl-under"
                                style={{ left: `${Math.min(r.start, 0.55) * 100}%` }}
                              >
                                {r.range}
                              </span>
                            ) : null}
                          </>
                        )}
                      </div>
                    </React.Fragment>
                  ))
                : null}
            </div>
          </div>
          {/* Below the timeline, so the Today line stops at the last row (UX review round 24). */}
          {anyMarks ? (
            <div className="today-tl-legend">
              <span>Marks show when campaign work is due. Hover one for what’s due.</span>
              <span>
                <i data-tone="late" />
                Overdue
              </span>
              <span>
                <i data-tone="soon" />
                Due in 2 days
              </span>
              <span>
                <i data-tone="later" />
                Later
              </span>
              <span>
                <i data-tone="ready" />
                All ready
              </span>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
};

/** Scheduled's first row while the agent works: "In progress · Your agent is …". */
const AgentRowBody: React.FC<{ doing: string }> = ({ doing }) => (
  <>
    <span className="today-row-when" data-tone="ok">
      <i />
      In progress
    </span>
    <span className="today-row-text">Your agent is {agentRowWords(doing)}</span>
  </>
);

/** Networks named in the Social header before the rest are counted. */
const NETWORKS_SHOWN = 3;

/**
 * "X · Threads · LinkedIn", or "Facebook · Instagram +5" when there are more:
 * all seven names cut to "Facebook · Instagram · Linke…" beside the link
 * (backlog 773d4dad). The full list is the tooltip.
 */
function networksLine(names: string[]): string {
  if (names.length <= NETWORKS_SHOWN) return names.join(' · ');
  return `${names.slice(0, 2).join(' · ')} +${names.length - 2}`;
}

// ── Phone ───────────────────────────────────────────────────────────────────

const PHONE = '(max-width: 899px)';

/** The prototype's `narrow`: Today's phone layout, below 900px. */
function usePhone() {
  const [phone, setPhone] = useState(() => window.matchMedia?.(PHONE).matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.(PHONE);
    if (!mq) return;
    const update = () => setPhone(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return phone;
}

/** Below the track: room for the dots. */
const DOTS_ROOM = 40;
const TRACK_MIN = 400;
/** The track's side padding (ink.css .today-sw). */
const SWIPE_INSET = 16;

/**
 * One row of cards you swipe through (App Prototype, phone Today): the next card
 * peeks in, each scrolls inside itself, and the row fills the screen down to its
 * dots. Height is measured, since what sits above it arrives late or goes away
 * (the approvals button, the alert bars, the first-run card).
 */
const TodaySwipe: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const cards = React.Children.toArray(children);
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  useEffect(() => {
    const track = trackRef.current;
    const main = track?.closest('.ink-main');
    const page = track?.closest('.today-page');
    if (!track || !main || !page) return;
    const fit = () => {
      const top =
        track.getBoundingClientRect().top - main.getBoundingClientRect().top + main.scrollTop;
      const height = Math.round(main.clientHeight - top - DOTS_ROOM);
      track.style.height = `${Math.max(TRACK_MIN, height)}px`;
    };
    fit();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    ro?.observe(main);
    ro?.observe(page);
    // Something above comes or goes (a bar dismissed, the first-run card closed)
    // without the page, which fills the screen, changing size.
    const mo = new MutationObserver((changes) => {
      if (changes.some((c) => !track.contains(c.target))) fit();
    });
    mo.observe(page, { childList: true, subtree: true });
    // The entrance moves the track while it's measured.
    page.addEventListener('animationend', fit);
    return () => {
      ro?.disconnect();
      mo.disconnect();
      page.removeEventListener('animationend', fit);
    };
  }, []);
  const cardEls = () => Array.from(trackRef.current?.children || []);
  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    // Where a snapped card's edge rests: inside the track's padding.
    const edge = track.getBoundingClientRect().left + SWIPE_INSET;
    let nearest = 0;
    let best = Infinity;
    cardEls().forEach((c, i) => {
      const d = Math.abs(c.getBoundingClientRect().left - edge);
      if (d < best) {
        best = d;
        nearest = i;
      }
    });
    // The last card can't reach the edge; at the end of the row it's the one showing.
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    setActive(atEnd ? cards.length - 1 : nearest);
  };
  const go = (i: number) => {
    const track = trackRef.current;
    const card = cardEls()[i];
    if (!track || !card) return;
    const left =
      track.scrollLeft +
      card.getBoundingClientRect().left -
      track.getBoundingClientRect().left -
      SWIPE_INSET;
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left, behavior: still ? 'auto' : 'smooth' });
  };
  const current = Math.min(active, cards.length - 1);
  return (
    <>
      <div className="today-sw" ref={trackRef} onScroll={onScroll} data-enter={1}>
        {cards.map((card, i) => (
          <div className="today-sw-card" key={i}>
            {card}
          </div>
        ))}
      </div>
      <div className="today-sw-dots" role="tablist" aria-label="Today cards" data-enter={2}>
        {cards.map((_, i) => (
          <button
            key={i}
            type="button"
            className="today-sw-dot"
            role="tab"
            aria-selected={i === current}
            aria-label={`Card ${i + 1} of ${cards.length}`}
            onClick={() => go(i)}
          >
            <span />
          </button>
        ))}
      </div>
    </>
  );
};

/** A bar's ✕: gone for this session, back in the next if it still applies. */
function useDismissed(key: string): [boolean, () => void] {
  const [gone, setGone] = useState(() => {
    try {
      return sessionStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  });
  const dismiss = () => {
    setGone(true);
    try {
      sessionStorage.setItem(key, '1');
    } catch {
      // Storage blocked: it stays gone until this page reloads.
    }
  };
  return [gone, dismiss];
}

/**
 * A notice as a full-width bar above the date (App Prototype, Today). The whole
 * bar runs its action; the ✕ is a sibling, so it only dismisses.
 */
const TodayBar: React.FC<{
  text: string;
  cta: string;
  to?: string;
  onClick?: () => void;
  onDismiss: () => void;
}> = ({ text, cta, to, onClick, onDismiss }) => {
  const body = (
    <>
      <span className="today-bar-text">{text}</span>
      <span className="today-bar-cta">{cta}</span>
    </>
  );
  return (
    <div className="today-bar" role="status">
      {to ? (
        <Link to={to} className="today-bar-main">
          {body}
        </Link>
      ) : (
        <button type="button" className="today-bar-main" onClick={onClick}>
          {body}
        </button>
      )}
      <button type="button" className="today-bar-x" aria-label="Dismiss" onClick={onDismiss}>
        ✕
      </button>
    </div>
  );
};

const soonWords = (n: number) =>
  n === 1 ? '1 campaign starts soon, not scheduled' : `${n} campaigns start soon, not scheduled`;

// ── Page ────────────────────────────────────────────────────────────────────

export const CmsToday: React.FC = () => {
  const { accountId } = useAccount();
  const phone = usePhone();
  const [busy, setBusy] = useState<string | null>(null);
  const {
    window: win,
    loading,
    reachLoading,
    error,
    data,
    posts,
    campaigns,
    allCampaigns,
    refetch,
  } = useToday();
  const review = useApprovals();
  const homeGapState = useSiteHomeGap(accountId);
  const homeWarning = homeGapState.gap ? homeNotice(homeGapState.gap) : null;
  const [soonGone, dismissSoon] = useDismissed(`today-bar-soon:${accountId}`);
  const [homeGone, dismissHome] = useDismissed(`today-bar-home:${accountId}`);
  // Nothing to approve but a campaign that won't start.
  const soonBar = !review.loading && !review.count && review.soon > 0 && !soonGone;
  const homeBar = homeWarning && !homeGone ? homeWarning : null;
  // Menu links to empty sections or nowhere, as Pages → Header shows them.
  const menuWarning = todayMenuNotice(data?.menuLinks);

  const agentWork = useAgentWork();
  const [unpublish] = useMutation(UNPUBLISH_TINYCMS_CONTENT);
  const { reachOn, atomizeOn } = useAddons();
  // Each running or upcoming campaign's open work, for the timeline's due marks.
  const { data: dueData } = useQuery<{ campaignDueWork: DueWork[] }>(CAMPAIGN_DUE_WORK, {
    variables: { accountId },
    skip: !accountId || !reachOn,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const dueWork = dueData?.campaignDueWork || [];
  const channelsQ = useQuery<{
    socialProfiles: {
      id: string;
      platform: string;
      profileName?: string | null;
      profileUsername?: string | null;
      isActive: boolean;
    }[];
  }>(REACH_CHANNELS, { variables: { accountId }, skip: !accountId || !reachOn });
  const contentLists = ['GetTinycmsArticles', 'GetTinycmsPages', 'GetTinycmsForms'];
  const { sites } = useTinycmsSites();
  const host = sites.find((x) => x.accountId === accountId)?.host || null;

  const from = useMemo(() => new Date(win.from), [win.from]);
  const approvals = useMemo(() => data?.approvals || [], [data]);
  const sent = useMemo(
    () =>
      posts.filter((p) => {
        const t = toDate(p.socialPublishDate);
        return (p.reachStatus === 'posted' || p.reachStatus === 'publishing') && t && t >= from;
      }),
    [posts, from]
  );

  // ── Recent changes ────────────────────────────────────────────────────────
  const wentOut = useMemo(() => {
    // Undo takes the item off the site; the site's own look can't be taken down.
    const undoOf = (a: TodayApproval | null) =>
      a && a.action === 'publish' && a.stillLive && a.kind !== 'site'
        ? async () => {
            const name = a.subjectTitle ? `“${a.subjectTitle}”` : 'this';
            const ok = await askInk({
              title: `Take ${name} off the site?`,
              body: 'You can publish it again later.',
              confirmLabel: 'Take it down',
              danger: true,
            });
            if (!ok) return;
            await unpublish({
              variables: {
                accountId,
                kind: a.kind,
                id: a.subjectId,
                reason: 'Undone from Today',
              },
              refetchQueries: contentLists,
            });
            showInkToast(`Took down ${name}.`);
          }
        : undefined;
    const rows: ChangeRow[] = groupApprovals(approvals).map((g) => {
      const a = g.latest;
      if (g.events.length === 1) {
        return {
          key: a.id,
          at: new Date(a.recordedAt),
          text: rowText(a),
          home: a.kind === 'page' && isHomePath(a.subjectPath) && !titledHome(a.subjectTitle),
          obj: [
            rowPlace(a),
            a.approvedBy
              ? `${a.action === 'unpublish' ? 'by' : 'approved by'} ${approverPhrase(a)}`
              : null,
            a.action === 'schedule' && a.scheduledFor
              ? `for ${when(new Date(a.scheduledFor))}`
              : null,
          ]
            .filter(Boolean)
            .join(' · '),
          undone: a.action === 'publish' && !a.stillLive,
          undo: undoOf(a),
        };
      }
      return {
        key: g.key,
        at: new Date(a.recordedAt),
        // Several approvals of one item: an update unless the first of them was v1.
        text: rowText(
          a,
          a.action === 'publish' && (g.events[g.events.length - 1].versionNumber || 0) > 1
        ),
        home: a.kind === 'page' && isHomePath(a.subjectPath) && !titledHome(a.subjectTitle),
        obj: [rowPlace(a), groupSummary(g, 'today')].filter(Boolean).join(' · '),
        undone: a.action === 'publish' && !g.live,
        undo: undoOf(g.live),
      };
    });
    for (const p of sent) {
      rows.push({
        key: `post:${p.id}`,
        at: toDate(p.socialPublishDate)!,
        text: `Posted to ${channelName(p.platform)}`,
        obj: [postApprovedBy(p), postText(p).split('\n')[0].trim()].filter(Boolean).join(' · '),
        ...(wentOutByRule(p) ? { rule: postRuleLabel(channelName(p.platform)) } : {}),
      });
    }
    return rows.sort((a, b) => b.at.getTime() - a.at.getTime());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvals, sent, accountId]);

  const undo = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    try {
      await fn();
      await refetch();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(null);
    }
  };

  // ── Scheduled: the next seven days ────────────────────────────────────────
  const upcoming: TodayScheduled[] = useMemo(() => {
    const now = Date.now();
    return (data?.scheduled || []).filter((s) => {
      const t = new Date(s.at).getTime();
      return t >= now && t < now + 7 * DAY;
    });
  }, [data]);

  // Recent changes include Reach, so they wait for it too.
  const queueLoading = loading || reachLoading;
  const agentLink = agentWork ? agentWorkLink(agentWork) : null;

  const perf = data?.performance || null;
  const websiteCell = (
    <WebsiteCell host={host} perf={perf} loading={loading} readerQuestions={atomizeOn} />
  );
  // Connected channels only, as Reach → Channels lists them: a disconnected
  // one keeps its row (and its account name above, for its old posts).
  const networks = [
    ...new Set(
      (channelsQ.data?.socialProfiles || [])
        .filter((c) => c.isActive)
        .map((c) => channelName(c.platform))
    ),
  ];
  const channelsLoading = reachOn && !channelsQ.data && !channelsQ.error;
  const socialCell = reachOn ? (
    <SocialCell networks={networks} channelsLoading={channelsLoading} ends={trendEnds(perf)} />
  ) : null;
  const campaignsSection = reachOn ? (
    <CampaignTimeline all={allCampaigns} waiting={campaigns} loading={reachLoading} due={dueWork} />
  ) : null;
  const recentChanges = (
    <section>
      <SectionHead label="Recent changes" sub="Today">
        <Link to="/settings/activity" className="today-sec-link">
          Changes <span aria-hidden>→</span>
        </Link>
      </SectionHead>
      <div className="today-list">
        {queueLoading ? (
          <InkSkeleton rows={3} label="Loading recent changes" />
        ) : wentOut.length ? (
          (phone ? wentOut : wentOut.slice(0, CHANGES_SHOWN)).map((e) => (
            <div key={e.key} className="today-row today-row-change">
              <span className="today-row-time">{time(e.at)}</span>
              <div className="today-row-main" data-undone={e.undone || undefined}>
                <div className="today-row-text">
                  {e.text}
                  {e.home ? <HomeTag /> : null}
                </div>
                {e.rule ? (
                  <Link to="/reach/channels" className="today-row-sub">
                    Your rule: {e.rule}
                  </Link>
                ) : e.obj ? (
                  <div className="today-row-sub">{e.obj}</div>
                ) : null}
              </div>
              {e.undo ? (
                <button
                  type="button"
                  className="today-undo"
                  disabled={busy === e.key}
                  onClick={() => undo(e.key, e.undo!)}
                >
                  Undo
                </button>
              ) : (
                <span />
              )}
            </div>
          ))
        ) : (
          <div className="today-list-empty">Nothing has changed yet today.</div>
        )}
      </div>
    </section>
  );
  const scheduled = (
    <section>
      <SectionHead label="Scheduled" sub="Next 7 days">
        <Link to="/content?view=articles&filter=scheduled" className="today-sec-link">
          Calendar →
        </Link>
      </SectionHead>
      <div className="today-list">
        {agentWork ? (
          agentLink ? (
            <Link to={agentLink.to} className="today-row today-row-next">
              <AgentRowBody doing={agentWork.doing} />
            </Link>
          ) : (
            <div className="today-row today-row-next">
              <AgentRowBody doing={agentWork.doing} />
            </div>
          )
        ) : null}
        {loading ? <InkSkeleton rows={2} label="Loading what's scheduled" /> : null}
        {(phone ? upcoming : upcoming.slice(0, SCHEDULED_SHOWN)).map((s) => (
          <Link
            key={`${s.kind}:${s.id}`}
            to={reviewHref(s.kind, s.id)}
            className="today-row today-row-next"
          >
            <span className="today-row-when">{when(new Date(s.at))}</span>
            <span className="today-row-text">{s.title || 'Untitled'}</span>
            <span className="today-row-cat">{s.cat || ''}</span>
          </Link>
        ))}
        {!upcoming.length && !loading && !agentWork ? (
          <div className="today-list-empty">Nothing scheduled.</div>
        ) : null}
      </div>
    </section>
  );

  return (
    <main className="ink-body today-page">
      {soonBar || homeBar ? (
        <div className="today-bars" data-enter={0}>
          {soonBar ? (
            <TodayBar
              text={soonWords(review.soon)}
              cta="Review →"
              onClick={() => review.open()}
              onDismiss={dismissSoon}
            />
          ) : null}
          {homeBar ? (
            <TodayBar
              text={homeBar.text}
              cta={homeBar.label}
              to={homeBar.to}
              onDismiss={dismissHome}
            />
          ) : null}
        </div>
      ) : null}
      {menuWarning ? (
        <div className="today-notices">
          <SiteHomeNotice
            text={menuWarning.text}
            action={{ label: menuWarning.label, to: menuWarning.to }}
          />
        </div>
      ) : null}

      {/* Blocks rise in one after another (Motion and Brand spec §2.3): title,
          Performance's head, its numbers, Campaigns, then the two lists. */}
      <div className="today-head" data-enter={0}>
        <div>
          <div className="today-date">
            <span className="today-date-long">{dayLabel()}</span>
            <span className="today-date-short">{shortDayLabel()}</span>
          </div>
          <h1 className="today-title ink-tabtitle">Today</h1>
        </div>
        {/* Approvals live in the review workspace now (design 2026-10-27): the
            header opens it, as the top bar and the Today tab do. A campaign that
            won't start is no all-clear, even once its bar is dismissed. */}
        {review.loading ? null : review.count ? (
          <ApprovalsReviewButton />
        ) : review.soon || homeBar ? null : (
          <span className="today-need">
            <i />
            Nothing awaiting approval
          </span>
        )}
      </div>

      <TodayAgentControls accountId={accountId} />

      {error && !data ? (
        <div className="ink-muted" role="alert" style={{ marginTop: 16, color: 'var(--ink-red)' }}>
          Today couldn’t load just now. What’s waiting is still in Website.{' '}
          <button type="button" className="today-undo" onClick={() => refetch()}>
            Try again
          </button>
        </div>
      ) : null}

      {phone ? (
        <TodaySwipe>
          {websiteCell}
          {socialCell}
          {campaignsSection}
          {scheduled}
          {recentChanges}
          <TodaySiteHealthCard />
        </TodaySwipe>
      ) : (
        <>
          <Performance
            socialOn={reachOn && !channelsLoading && networks.length > 0}
            website={websiteCell}
            social={socialCell}
          />
          {campaignsSection}
          <div className="today-pair" data-enter={4}>
            {recentChanges}
            {scheduled}
          </div>
        </>
      )}
    </main>
  );
};
